import { z } from 'zod';

/* ------------------------------------------------------------------ */
/* GeoJSON (WGS84, [longitude, latitude])                              */
/* ------------------------------------------------------------------ */

/** Hard cap on vertices per submitted shape, so one request cannot exhaust the database. */
export const MAX_GEOMETRY_POSITIONS = 10_000;

export const PositionSchema = z.tuple([z.number().min(-180).max(180), z.number().min(-90).max(90)]);
export type Position = z.infer<typeof PositionSchema>;

export const LinearRingSchema = z
  .array(PositionSchema)
  .min(4, 'A ring needs at least 4 positions')
  .refine((ring) => {
    const first = ring[0];
    const last = ring[ring.length - 1];
    return (
      first !== undefined && last !== undefined && first[0] === last[0] && first[1] === last[1]
    );
  }, 'A ring must start and end at the same position');

export const PolygonSchema = z.object({
  type: z.literal('Polygon'),
  coordinates: z.array(LinearRingSchema).min(1),
});

export const MultiPolygonSchema = z.object({
  type: z.literal('MultiPolygon'),
  coordinates: z.array(z.array(LinearRingSchema).min(1)).min(1),
});

export const LineStringSchema = z.object({
  type: z.literal('LineString'),
  coordinates: z.array(PositionSchema).min(2),
});

function countPositions(value: unknown): number {
  if (!Array.isArray(value)) return 0;
  if (value.length === 2 && typeof value[0] === 'number') return 1;
  return value.reduce<number>((sum, item) => sum + countPositions(item), 0);
}

const withinPositionCap = (geometry: { coordinates: unknown }) =>
  countPositions(geometry.coordinates) <= MAX_GEOMETRY_POSITIONS;
const CAP_MESSAGE = `A shape may have at most ${MAX_GEOMETRY_POSITIONS} points`;

/** Area geometry accepted for towns, zones and restriction areas. */
export const AreaGeometrySchema = z
  .discriminatedUnion('type', [PolygonSchema, MultiPolygonSchema])
  .refine(withinPositionCap, CAP_MESSAGE);
export type AreaGeometry = z.infer<typeof AreaGeometrySchema>;

/** Restriction areas may also be drawn as a line (a road) plus a buffer. */
export const RestrictionGeometrySchema = z
  .discriminatedUnion('type', [PolygonSchema, MultiPolygonSchema, LineStringSchema])
  .refine(withinPositionCap, CAP_MESSAGE);
export type RestrictionGeometry = z.infer<typeof RestrictionGeometrySchema>;

/* ------------------------------------------------------------------ */
/* Countries                                                           */
/* ------------------------------------------------------------------ */

export const COUNTRY_CODES = ['IN', 'NP'] as const;
export const CountryCodeSchema = z.enum(COUNTRY_CODES);
export type CountryCode = z.infer<typeof CountryCodeSchema>;

export const CountrySchema = z.object({
  code: CountryCodeSchema,
  name: z.string(),
  currency: z.string().length(3),
  minorUnit: z.number().int(),
  taxRegime: z.enum(['GST', 'VAT']),
  timezone: z.string(),
  dialCode: z.string(),
  isActive: z.boolean(),
});
export type Country = z.infer<typeof CountrySchema>;

/* ------------------------------------------------------------------ */
/* Towns + service hours                                               */
/* ------------------------------------------------------------------ */

export const TOWN_STATUSES = ['planned', 'live', 'paused'] as const;
export const TownStatusSchema = z.enum(TOWN_STATUSES);
export type TownStatus = z.infer<typeof TownStatusSchema>;

export const SlugSchema = z
  .string()
  .min(2)
  .max(60)
  .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, 'Use lowercase letters, digits and single hyphens');

/** One day of trading. Minutes are local wall-clock minutes from that day's midnight. */
export const ServiceDaySchema = z
  .object({
    weekday: z.number().int().min(0).max(6), // 0 = Sunday
    isClosed: z.boolean(),
    opensMin: z.number().int().min(0).max(1439),
    checkoutClosesMin: z.number().int().min(1).max(2160),
    deliveryEndsMin: z.number().int().min(1).max(2160),
  })
  .refine((d) => d.opensMin < d.checkoutClosesMin, {
    message: 'Checkout must close after opening',
    path: ['checkoutClosesMin'],
  })
  .refine((d) => d.checkoutClosesMin <= d.deliveryEndsMin, {
    message: 'Deliveries cannot end before checkout closes',
    path: ['deliveryEndsMin'],
  })
  .refine((d) => d.deliveryEndsMin - d.opensMin <= 1440, {
    message: 'A trading day cannot be longer than 24 hours',
    path: ['deliveryEndsMin'],
  });
export type ServiceDay = z.infer<typeof ServiceDaySchema>;

export const ServiceWeekSchema = z
  .array(ServiceDaySchema)
  .length(7)
  .refine(
    (days) => new Set(days.map((d) => d.weekday)).size === 7,
    'Give each weekday (0–6) exactly once',
  );

export const TownSchema = z.object({
  id: z.uuid(),
  countryCode: CountryCodeSchema,
  slug: z.string(),
  name: z.string(),
  region: z.string().nullable(),
  timezone: z.string(),
  centroid: PositionSchema.nullable(),
  hasBoundary: z.boolean(),
  status: TownStatusSchema,
  version: z.number().int(),
  liveZoneCount: z.number().int(),
  serviceHours: z.array(ServiceDaySchema),
  updatedAt: z.string(),
});
export type Town = z.infer<typeof TownSchema>;

export const CreateTownInputSchema = z.object({
  countryCode: CountryCodeSchema,
  slug: SlugSchema,
  name: z.string().trim().min(2).max(80),
  region: z.string().trim().min(2).max(80).nullable().optional(),
  timezone: z.string().trim().min(3).max(64).nullable().optional(),
  centroid: PositionSchema.nullable().optional(),
});
export type CreateTownInput = z.infer<typeof CreateTownInputSchema>;

export const UpdateTownInputSchema = z
  .object({
    expectedVersion: z.number().int().min(1),
    name: z.string().trim().min(2).max(80).optional(),
    region: z.string().trim().min(2).max(80).nullable().optional(),
    timezone: z.string().trim().min(3).max(64).nullable().optional(),
    centroid: PositionSchema.nullable().optional(),
  })
  .refine(
    (v) => Object.keys(v).some((key) => key !== 'expectedVersion'),
    'Send at least one field to change',
  );
export type UpdateTownInput = z.infer<typeof UpdateTownInputSchema>;

export const SetTownStatusInputSchema = z.object({
  expectedVersion: z.number().int().min(1),
  status: TownStatusSchema,
});
export type SetTownStatusInput = z.infer<typeof SetTownStatusInputSchema>;

export const BuildBoundaryInputSchema = z.object({
  expectedVersion: z.number().int().min(1),
  /** Margin added around the union of the town's wards, for GPS wobble at the edge. */
  bufferMeters: z.number().int().min(0).max(2000),
});
export type BuildBoundaryInput = z.infer<typeof BuildBoundaryInputSchema>;

/* ------------------------------------------------------------------ */
/* Zones                                                               */
/* ------------------------------------------------------------------ */

export const ZONE_STATUSES = ['draft', 'live', 'paused'] as const;
export const ZoneStatusSchema = z.enum(ZONE_STATUSES);
export type ZoneStatus = z.infer<typeof ZoneStatusSchema>;

export const ZoneCodeSchema = z
  .string()
  .min(2)
  .max(32)
  .regex(/^[A-Z0-9]+(?:-[A-Z0-9]+)*$/, 'Use capital letters, digits and single hyphens');

export const ZoneSchema = z.object({
  id: z.uuid(),
  townId: z.uuid(),
  code: z.string(),
  name: z.string(),
  status: ZoneStatusSchema,
  version: z.number().int(),
  areaSqKm: z.number(),
  geometry: MultiPolygonSchema,
  publishedAt: z.string().nullable(),
  updatedAt: z.string(),
});
export type Zone = z.infer<typeof ZoneSchema>;

export const CreateZoneInputSchema = z.object({
  townId: z.uuid(),
  code: ZoneCodeSchema,
  name: z.string().trim().min(2).max(80),
  geometry: AreaGeometrySchema,
});
export type CreateZoneInput = z.infer<typeof CreateZoneInputSchema>;

export const UpdateZoneInputSchema = z
  .object({
    expectedVersion: z.number().int().min(1),
    name: z.string().trim().min(2).max(80).optional(),
    geometry: AreaGeometrySchema.optional(),
  })
  .refine(
    (v) => v.name !== undefined || v.geometry !== undefined,
    'Send a new name or a new shape',
  );
export type UpdateZoneInput = z.infer<typeof UpdateZoneInputSchema>;

export const ZoneTransitionInputSchema = z.object({
  expectedVersion: z.number().int().min(1),
});
export type ZoneTransitionInput = z.infer<typeof ZoneTransitionInputSchema>;

export const ZoneOverlapSchema = z.object({
  zoneId: z.uuid(),
  code: z.string(),
  status: ZoneStatusSchema,
  overlapSqM: z.number(),
});
export type ZoneOverlap = z.infer<typeof ZoneOverlapSchema>;

/* ------------------------------------------------------------------ */
/* Restriction areas                                                   */
/* ------------------------------------------------------------------ */

export const RESTRICTION_KINDS = ['no_service', 'no_motorbike', 'slow'] as const;
export const RestrictionKindSchema = z.enum(RESTRICTION_KINDS);
export type RestrictionKind = z.infer<typeof RestrictionKindSchema>;

export const CreateRestrictionInputSchema = z
  .object({
    townId: z.uuid(),
    name: z.string().trim().min(2).max(80),
    kind: RestrictionKindSchema,
    /** Only for `slow`: multiply normal travel speed by this (0.1–1). */
    speedFactor: z.number().min(0.1).max(1).nullable().optional(),
    geometry: RestrictionGeometrySchema,
    /** Required when the shape is a line: width either side of the road, in metres. */
    bufferMeters: z.number().int().min(0).max(1000).default(0),
    /** Optional daily window in local minutes; may wrap past midnight. Null = all day. */
    activeFromMin: z.number().int().min(0).max(1439).nullable().optional(),
    activeToMin: z.number().int().min(0).max(1439).nullable().optional(),
  })
  .refine((v) => v.kind !== 'slow' || typeof v.speedFactor === 'number', {
    message: 'A slow area needs a speed factor',
    path: ['speedFactor'],
  })
  .refine((v) => v.geometry.type !== 'LineString' || v.bufferMeters > 0, {
    message: 'A line needs a buffer of at least 1 metre',
    path: ['bufferMeters'],
  })
  .refine(
    (v) =>
      (v.activeFromMin === null || v.activeFromMin === undefined) ===
      (v.activeToMin === null || v.activeToMin === undefined),
    { message: 'Give both window ends or neither', path: ['activeToMin'] },
  );
export type CreateRestrictionInput = z.infer<typeof CreateRestrictionInputSchema>;

export const RestrictionSchema = z.object({
  id: z.uuid(),
  townId: z.uuid(),
  name: z.string(),
  kind: RestrictionKindSchema,
  speedFactor: z.number().nullable(),
  activeFromMin: z.number().int().nullable(),
  activeToMin: z.number().int().nullable(),
  isActive: z.boolean(),
  geometry: MultiPolygonSchema,
  updatedAt: z.string(),
});
export type Restriction = z.infer<typeof RestrictionSchema>;

/* ------------------------------------------------------------------ */
/* Serviceability                                                      */
/* ------------------------------------------------------------------ */

export const ServiceabilityQuerySchema = z.object({
  lat: z.coerce.number().min(-90).max(90),
  lng: z.coerce.number().min(-180).max(180),
});
export type ServiceabilityQuery = z.infer<typeof ServiceabilityQuerySchema>;

export const UNSERVICEABLE_REASONS = [
  'outside_coverage',
  'town_paused',
  'restricted',
  'no_hours_configured',
  /** W23: the town switch for ordering is not open to this person yet ("coming soon" + area vote). */
  'coming_soon',
] as const;

export const TradingStateSchema = z.object({
  acceptingOrders: z.boolean(),
  delivering: z.boolean(),
  /** ISO time checkout closes today, when currently accepting orders. */
  checkoutClosesAt: z.string().nullable(),
  /** ISO time orders open next, when not currently accepting orders. */
  nextOpensAt: z.string().nullable(),
});
export type TradingState = z.infer<typeof TradingStateSchema>;

export const ServiceabilitySchema = z.object({
  serviceable: z.boolean(),
  reason: z.enum(UNSERVICEABLE_REASONS).nullable(),
  countryCode: CountryCodeSchema.nullable(),
  town: z.object({ id: z.uuid(), slug: z.string(), name: z.string() }).nullable(),
  zone: z.object({ id: z.uuid(), code: z.string(), name: z.string() }).nullable(),
  trading: TradingStateSchema.nullable(),
  vehicleLimits: z.array(z.literal('no_motorbike')),
  speedFactor: z.number().nullable(),
});
export type Serviceability = z.infer<typeof ServiceabilitySchema>;

export interface FeatureCollection<P> {
  type: 'FeatureCollection';
  features: Array<{
    type: 'Feature';
    id: string;
    geometry: z.infer<typeof MultiPolygonSchema>;
    properties: P;
  }>;
}
